import { useState, useMemo } from 'react'
import { Card, Table, Button, Input, Space, App, Spin, Alert } from 'antd'
import { SearchOutlined, SwapOutlined } from '@ant-design/icons'
import { useCurrentUser } from '@/hooks'
import { authService } from '@/services/authService'
import type { Organization } from '@/types/milestone.types'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

/**
 * RP Admin - Organization Switcher Page
 *
 * Allows rp_admin and rp_support users to switch between any organization
 * or clear the org context entirely.
 */
function OrgSwitchPage() {
  const { message } = App.useApp()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: user, isLoading: userLoading } = useCurrentUser()
  const [searchText, setSearchText] = useState('')
  const [switching, setSwitching] = useState(false)

  // Filter organizations based on search text
  const filteredOrgs = useMemo(() => {
    if (!user?.organizations) return []

    if (!searchText.trim()) {
      return user.organizations
    }

    const search = searchText.toLowerCase()
    return user.organizations.filter(org =>
      org.id.toLowerCase().includes(search) ||
      org.name.toLowerCase().includes(search)
    )
  }, [user?.organizations, searchText])

  const handleSwitch = async (orgId: string, orgName: string) => {
    setSwitching(true)

    try {
      console.log('Switching to organization:', orgName, orgId)

      const result = await authService.switchOrganization(orgId)

      if (!result.success) {
        message.error(`Failed to switch to ${orgName}`)
        return
      }

      // Success - clear all caches as tenant data is completely different
      console.log('Clearing all caches for tenant switch...')
      queryClient.clear()

      message.success(`Switched to organization: ${orgName}`)

      // Navigate to home page to load new org data
      navigate('/')
    } catch (error) {
      console.error('Organization switch error:', error)
      message.error('An unexpected error occurred while switching organizations')
    } finally {
      setSwitching(false)
    }
  }

  // Check if user has required role
  const hasAccess = user?.globalRoles?.includes('rp_admin') || user?.globalRoles?.includes('rp_support')

  if (userLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    )
  }

  if (!hasAccess) {
    return (
      <Card>
        <Alert
          message="Access Denied"
          description="You do not have permission to access this page. Only RP Admin and Support users can switch organizations."
          type="error"
          showIcon
        />
      </Card>
    )
  }

  const columns = [
    {
      title: 'Org ID',
      dataIndex: 'id',
      key: 'id',
      width: '40%',
      sorter: (a: Organization, b: Organization) => a.id.localeCompare(b.id),
    },
    {
      title: 'Org Name',
      dataIndex: 'name',
      key: 'name',
      width: '40%',
      sorter: (a: Organization, b: Organization) => a.name.localeCompare(b.name),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '20%',
      render: (_: any, record: Organization) => {
        const isCurrent = record.id === user?.currentOrganization?.id

        return (
          <Button
            type={isCurrent ? 'default' : 'primary'}
            icon={<SwapOutlined />}
            onClick={() => handleSwitch(record.id, record.name)}
            disabled={switching || isCurrent}
            loading={switching}
          >
            {isCurrent ? 'Current' : 'Switch'}
          </Button>
        )
      },
    },
  ]

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <Card title="Organization Switcher">
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {user?.currentOrganization && (
            <Alert
              message="Current Organization"
              description={`${user.currentOrganization.name} (${user.currentOrganization.id})`}
              type="info"
              showIcon
            />
          )}

          <Input
            placeholder="Search by Org ID or Name..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            size="large"
          />

          <Table
            columns={columns}
            dataSource={filteredOrgs}
            rowKey="id"
            pagination={{
              defaultPageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} organizations`,
            }}
            loading={switching}
          />
        </Space>
      </Card>
    </div>
  )
}

export default OrgSwitchPage
